import React from 'react'
import {
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
} from '@mui/material'
import { Button } from 'components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlusCircle,
  faMinusCircle,
  faCheckCircle,
  faTimesCircle,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons'

import { removeDuplicates } from 'utils/remove-duplicates'

import colors from 'styles/colors'
import intl from 'localization/components'
import { ConditionallyRender, ImportDropzone } from 'components'
import { FormattedMessage, useIntl } from 'react-intl'
import ActionDialog from './action-dialog'
import { styles } from './styles/add-bulk-users'

interface User {
  userName: string
  email: string
}

interface SelectFieldsProps {
  selectedFields: string[]
  setSelectedFields: (fields: string[]) => void
  rows: Record<string, string>[]
}

const SelectFields = ({ selectedFields, setSelectedFields, rows }: SelectFieldsProps) => {
  return (
    <ConditionallyRender
      condition={selectedFields.length === 0}
      when={
        <Box>
          <Typography>Select User Name field</Typography>
          <Box>
            {Object.keys(rows[0] ?? {}).map((x) => (
              <List key={x} sx={{ width: '100%', bgcolor: 'background.paper' }}>
                <ListItem
                  component="div"
                  onClick={() => setSelectedFields([...selectedFields, x])}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemText primary={x} secondary={rows[0][x]} />
                </ListItem>
              </List>
            ))}
          </Box>
        </Box>
      }
      otherwise={
        <Box>
          <Typography>Select Email field</Typography>
          <Box>
            {Object.keys(rows?.[0] ?? {}).map((x) =>
              !selectedFields.includes(x) ? (
                <List key={x} sx={{ width: '100%', bgcolor: 'background.paper' }}>
                  <ListItem
                    component="div"
                    onClick={() => setSelectedFields([...selectedFields, x])}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ListItemText primary={x} secondary={rows?.[0][x]} />
                  </ListItem>
                </List>
              ) : null,
            )}
          </Box>
        </Box>
      }
    />
  )
}

interface AddBulkUsersProps {
  users: User[]
  setUsers: (users: User[]) => void
  action: () => void
}

const AddBulkUsers = ({ users, setUsers, action }: AddBulkUsersProps): JSX.Element => {
  const reactIntl = useIntl()
  const messages = reactIntl.messages
  const [rows, setRows] = React.useState<Record<string, string>[]>([])
  const [selectedFields, setSelectedFields] = React.useState<string[]>([])
  const [validateUsers, setValidateUsers] = React.useState(false)

  const updateTextField = (index: number, key: string, value: string) => {
    setUsers(
      users.map((x, i) => {
        if (i !== index) {
          return x
        } else {
          return {
            ...x,
            [key]: value,
          }
        }
      }),
    )
  }

  const handleSetFieldList = React.useCallback((newRows: Record<string, string>[]) => {
    setRows(newRows)
    setSelectedFields([])
  }, [])

  const restoreDefaults = () => {
    setRows([])
    setSelectedFields([])
  }

  React.useEffect(() => {
    if (selectedFields.length === 2) {
      const name = selectedFields[0]
      const email = selectedFields[1]
      setUsers(
        [
          ...users,
          ...rows.map((x) => ({
            userName: x[name],
            email: x[email],
          })),
        ].filter((x) => x.userName !== ''),
      )
      restoreDefaults()
    }
  }, [rows, selectedFields, setUsers, users])

  function validateEmail(email: string) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
  }

  const validate = React.useCallback(
    (index: number, ret?: string): boolean | string => {
      const value = users[index].email
      if (value.length) {
        if (validateEmail(value)) {
          const sameEmails = users.filter(({ email }) => email === value)
          if (sameEmails.length < 2) {
            return ret === 'bool' ? true : ''
          } else {
            return ret === 'bool'
              ? false
              : 'Duplicate emails! Click the X to delete duplicates!'
          }
        } else {
          return ret === 'bool' ? false : 'Invalid email format!'
        }
      } else {
        return ret === 'bool' ? false : 'Empty field'
      }
    },
    [users],
  )

  React.useEffect(() => {
    const valid = users.filter((x, i) => validate(i))
    const doesHaveName = users?.some((user) => !!user.userName)

    const isValid = valid?.length === 0 && doesHaveName

    setValidateUsers(isValid)
  }, [setValidateUsers, users, validate])

  const handleIconClick = (i: number) => {
    if (
      validate(i, 'text') ===
      'Duplicate emails! Click the X to delete duplicates!'
    ) {
      setUsers(removeDuplicates<User>(users, 'email'))
    }
  }

  return (
    <ActionDialog
      disabled={!validateUsers}
      title={
        <FormattedMessage
          id={intl.riskMonitoringNew('add-to-monitoring-list-title')}
        />
      }
      content={() => (
        <Box sx={{ margin: '50px', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ImportDropzone onFileLoaded={handleSetFieldList} />
            <ConditionallyRender
              condition={rows && selectedFields.length < 2}
              when={
                <SelectFields
                  selectedFields={selectedFields}
                  setSelectedFields={setSelectedFields}
                  rows={rows}
                />
              }
            />
            {users.map((user, i) => (
              <React.Fragment key={user.email}>
                <Box sx={styles.userIcon}>
                  <ConditionallyRender
                    condition={i > 0}
                    when={
                      <IconButton
                        sx={{ color: 'red' }}
                        onClick={() =>
                          setUsers(users.filter((x, index) => index !== i))
                        }
                      >
                        <FontAwesomeIcon icon={faMinusCircle} />
                      </IconButton>
                    }
                  />
                  <Typography>User {i + 1}</Typography>
                </Box>
                <Box
                  sx={
                    users.length > 1
                      ? styles.multiUserContainer
                      : styles.userContainer
                  }
                  key={`${user.email}-${user.userName}`}
                >
                  <TextField
                    data-cy="first-name-input"
                    multiline
                    value={user.userName}
                    type="text"
                    label={messages[intl.generic('first-name')] as string}
                    sx={{ width: '100%' }}
                    onChange={(e) =>
                      updateTextField(i, 'userName', e.target.value)
                    }
                    margin="normal"
                    variant="outlined"
                  />
                  <Box sx={styles.validationContainer}>
                    <TextField
                      data-cy="email-input"
                      sx={{ flex: 1 }}
                      multiline
                      value={user.email}
                      type="email"
                      label={messages[intl.generic('emails')] as string}
                      onChange={(e) =>
                        updateTextField(i, 'email', e.target.value)
                      }
                      margin="normal"
                      variant="outlined"
                    />
                    <FontAwesomeIcon
                      onClick={() => handleIconClick(i)}
                      style={{
                        color: validate(i, 'bool')
                          ? colors?.bg_rating_10
                          : colors?.bg_rating_1,
                        cursor:
                          validate(i, 'text') ===
                          'Duplicate emails! Click the X to delete duplicates!'
                            ? 'pointer'
                            : 'default',
                        ...(styles.validationIcon as React.CSSProperties),
                      }}
                      icon={validate(i, 'bool') ? faCheckCircle : faTimesCircle}
                    />
                    <Typography sx={styles.validationText}>
                      {validate(i, 'text') as string}
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </Box>
          <IconButton
            onClick={() => setUsers([...users, { userName: '', email: '' }])}
          >
            <FontAwesomeIcon icon={faPlusCircle} />
          </IconButton>
        </Box>
      )}
      action={action}
      maxWidth="sm"
      fullWidth={true}
      render={(open) => (
        <Button
          data-cy="add-bulk-users-button"
          icon={<FontAwesomeIcon icon={faUserPlus} />}
          style={{
            backgroundColor: colors?.risikaTertiary,
            color: 'white',
            alignSelf: 'center',
          }}
          variant="contained"
          onClick={open}
        >
          Add bulk users
        </Button>
      )}
      agreeText={<FormattedMessage id={intl.riskMonitoringNew('add')} />}
    />
  )
}

export default AddBulkUsers
